import { useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../cn.js'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: ReactNode
  error?: string
}

export function Checkbox({ label, error, className, required, ...rest }: CheckboxProps) {
  const id = useId()
  const errorId = `${id}-error`

  return (
    <div className="flex flex-col gap-1">
      {/* The whole row is the label, so the tap target is the text as well as
          the 20px box — essential on mobile (§11.6). */}
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3 py-1.5">
        <input
          id={id}
          type="checkbox"
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'mt-0.5 h-5 w-5 shrink-0 rounded border-2 accent-brand',
            error ? 'border-danger' : 'border-line-strong',
            className,
          )}
          {...rest}
        />
        <span className="text-sm text-ink">{label}</span>
      </label>

      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}
